import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectVorlagen,
  selectKategorien,
  createVorlage,
  updateVorlage,
  deleteVorlage,
  createTodoFromVorlage,
} from 'modules/todo/features/todoSlice';
import { prioMeta, findeKategorie } from './todoConstants';
import VorlageForm from './VorlageForm';

/**
 * Panel zum Verwalten der Vorlagen und zum Erzeugen einer Aufgabe aus einer Vorlage.
 *
 * @param {{ onAufgabeErzeugt: () => void }} props
 */
export default function VorlagenPanel({ onAufgabeErzeugt }) {
  const dispatch = useDispatch();
  const vorlagen = useSelector(selectVorlagen);
  const kategorien = useSelector(selectKategorien);

  const [formOffen, setFormOffen] = useState(false);
  const [bearbeitet, setBearbeitet] = useState(null);

  const oeffneNeu = () => {
    setBearbeitet(null);
    setFormOffen(true);
  };

  const oeffneBearbeiten = (vorlage) => {
    setBearbeitet(vorlage);
    setFormOffen(true);
  };

  const speichern = (daten) => {
    if (bearbeitet) {
      dispatch(updateVorlage({ uuid: bearbeitet.uuid, vorlage: daten }));
    } else {
      dispatch(createVorlage(daten));
    }
    setFormOffen(false);
  };

  const uebernehmen = (vorlage) => {
    dispatch(createTodoFromVorlage(vorlage.uuid));
    onAufgabeErzeugt();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button type="button" onClick={oeffneNeu} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500">
          + Neue Vorlage
        </button>
      </div>

      {vorlagen.length === 0 ? (
        <p className="rounded-xl border border-dashed border-line bg-paper-soft p-8 text-center text-ink-muted">
          Noch keine Vorlagen. Lege eine an, um wiederkehrende Aufgaben mit festen Unteraufgaben schnell zu erstellen.
        </p>
      ) : (
        <div className="space-y-2">
          {vorlagen.map((v) => {
            const prio = prioMeta(v.prioritaet);
            const kategorie = findeKategorie(kategorien, v.kategorieId);
            return (
              <div key={v.uuid} className="rounded-xl border border-line bg-paper-soft p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-ink">{v.name}</span>
                  <span className={`rounded-full px-2 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wide ring-1 ${prio.badge}`}>
                    {prio.label}
                  </span>
                  {kategorie && (
                    <span className="inline-flex items-center gap-1 font-mono text-xs text-ink-muted">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: kategorie.farbe }} />
                      {kategorie.name}
                    </span>
                  )}
                  <span className="font-mono text-xs text-ink-muted">
                    {v.unteraufgaben?.length ?? 0} Unteraufgaben
                  </span>
                  <div className="ml-auto flex gap-1">
                    <button type="button" onClick={() => uebernehmen(v)} className="rounded-lg bg-brand-500/10 px-3 py-1 text-xs font-medium text-brand-700 ring-1 ring-brand-500/25 transition-colors hover:bg-brand-500/20">
                      Als Aufgabe
                    </button>
                    <button type="button" onClick={() => oeffneBearbeiten(v)} className="rounded-lg px-2 py-1 text-ink-muted hover:bg-ink/5 hover:text-ink" aria-label="Bearbeiten">
                      ✎
                    </button>
                    <button
                      type="button"
                      onClick={() => window.confirm(`Vorlage „${v.name}" löschen?`) && dispatch(deleteVorlage(v.uuid))}
                      className="rounded-lg px-2 py-1 text-ink-muted hover:bg-loss/10 hover:text-loss"
                      aria-label="Löschen"
                    >
                      🗑
                    </button>
                  </div>
                </div>
                {v.beschreibung && <p className="mt-1 text-sm text-ink-soft">{v.beschreibung}</p>}
                {v.unteraufgaben?.length > 0 && (
                  <ul className="mt-2 list-inside list-disc text-sm text-ink-muted">
                    {v.unteraufgaben.map((u, i) => (
                      <li key={i}>{u.titel}</li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {formOffen && (
        <VorlageForm
          vorlage={bearbeitet}
          kategorien={kategorien}
          onSubmit={speichern}
          onClose={() => setFormOffen(false)}
        />
      )}
    </div>
  );
}
